import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";

/**
 * LicenseModal - Modal for creating or renewing a license
 *
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} toggle - Function to close the modal
 * @param {string} mode - "create" or "renew"
 * @param {object} licenseData - Existing license data (for renew mode)
 * @param {function} onSubmit - Callback with form data
 */
const LicenseModal = ({ isOpen, toggle, mode = "create", licenseData = null, onSubmit }) => {
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    level: "GOLD",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (mode === "renew" && licenseData) {
        setFormData({
          startDate: licenseData.startDate || "",
          endDate: licenseData.endDate || "",
          level: licenseData.level || "GOLD",
        });
      } else {
        const today = new Date().toISOString().split("T")[0];
        const nextYear = new Date();
        nextYear.setFullYear(nextYear.getFullYear() + 1);
        setFormData({
          startDate: today,
          endDate: nextYear.toISOString().split("T")[0],
          level: "GOLD",
        });
      }
      setErrors({});
    }
  }, [isOpen, mode, licenseData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (mode === "create") {
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.level) newErrors.level = "License level is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    } else if (formData.startDate && formData.endDate < formData.startDate) {
      newErrors.endDate = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (mode === "create") {
        await onSubmit({ startDate: formData.startDate, endDate: formData.endDate, level: formData.level });
      } else {
        await onSubmit(formData.endDate);
      }
      toggle();
    } catch (error) {
      console.error("License operation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        {mode === "create" ? "Create New License" : "Renew License"}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {mode === "renew" && licenseData && (
            <FormGroup>
              <Label>License Level</Label>
              <Input type="text" value={licenseData.levelDisplayName || licenseData.level} disabled />
            </FormGroup>
          )}

          {mode === "create" && (
            <>
              <FormGroup>
                <Label for="level">
                  License Level <span className="text-danger">*</span>
                </Label>
                <Input
                  id="level"
                  type="select"
                  value={formData.level}
                  onChange={(e) => handleChange("level", e.target.value)}
                  invalid={!!errors.level}
                  disabled={loading}
                >
                  <option value="SILVER">Silver</option>
                  <option value="GOLD">Gold</option>
                  <option value="PLATINUM">Platinum</option>
                </Input>
                <FormFeedback>{errors.level}</FormFeedback>
              </FormGroup>

              <FormGroup>
                <Label for="startDate">
                  Start Date <span className="text-danger">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange("startDate", e.target.value)}
                  invalid={!!errors.startDate}
                  disabled={loading}
                />
                <FormFeedback>{errors.startDate}</FormFeedback>
              </FormGroup>
            </>
          )}

          {mode === "renew" && (
            <FormGroup>
              <Label>Current Start Date</Label>
              <Input type="date" value={formData.startDate} disabled />
            </FormGroup>
          )}

          <FormGroup>
            <Label for="endDate">
              {mode === "renew" ? "New End Date" : "End Date"} <span className="text-danger">*</span>
            </Label>
            <Input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleChange("endDate", e.target.value)}
              invalid={!!errors.endDate}
              disabled={loading}
            />
            <FormFeedback>{errors.endDate}</FormFeedback>
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {mode === "create" ? "Creating..." : "Renewing..."}
              </>
            ) : mode === "create" ? (
              "Create License"
            ) : (
              "Renew License"
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default LicenseModal;
